// The channel screens' routes.
//
// **Every family here is served.** The campaigns, the audiences, the promo codes and the
// notifications tab were routed only behind the development switch (`pendingScreensEnabled`) while
// their modules were answered by mocks, on the owner's decision of 2026-09-30; each joined the
// routes below the day its backend landed.
//
// **The price catalogue is not a screen of its own.** Its tabs are sibling routes that each draw
// the same tab bar (`useProductCatalogTabs`), so the live plans screen keeps its route and its
// name, `websites:subscriptions`, which four screens push to; `sales/products` only redirects
// there.
//
// **So is one more piece of that decision**: a channel's Settings › Notifications tab. A member's
// Statistics tab was one until its channel read was served.

//
// **The gates are permission keys, never role names.** `meta.requires` names what the reads
// behind the screen ask the server for, in `base-auth`'s spelling, so a route and its reads
// refuse the same caller. `websites.website.manage` is the one the channel's own row is gated on
// — AD3 declares no `view` verb for a channel, so a caller without it gets `403` on the reads
// too and a screen drawn for them would be a screen of refusals.
//
// A child inherits the meta of the record above it unless it declares its own. The screens still
// on the previous backend therefore inherit the layout's gate, which is the same authority they
// will ask for once they move.

import type { RouteRecordRaw } from 'vue-router'
import { OrgPermissions } from '@/types/permissions'

/**
 * The sales screens. Their reads take `websites.storefront.edit` — the key the storefront's own
 * screens take. **Served** since 2026-10-11 (`docs/plans/billing-ui.md` 8.3, 8.4), and linked
 * from the dashboard, the monetization settings and an item's tabs in every build since the
 * owner's Q24 of 2026-10-15.
 */
const salesGate = { permissions: OrgPermissions.WEBSITES_STOREFRONT_EDIT }

/**
 * The channel screens whose reads take a key beyond the channel's own. A child's `requires`
 * replaces its layout's, so each names `websites.website.manage` again beside its own key; without
 * one, the screen opened empty — « add your first item » — and every write on it was refused.
 */
const catalogueGate = {
  permissions: [OrgPermissions.WEBSITES_WEBSITE_MANAGE, OrgPermissions.WEBSITES_CATALOGUE_EDIT],
}
const commentsGate = {
  permissions: [OrgPermissions.WEBSITES_WEBSITE_MANAGE, OrgPermissions.WEBSITES_COMMENTS_MODERATE],
}
const blogGate = {
  permissions: [OrgPermissions.WEBSITES_WEBSITE_MANAGE, OrgPermissions.WEBSITES_PAGES_EDIT],
}

const salesRoutes: RouteRecordRaw[] = [
  {
    path: 'sales',
    redirect: (to) => ({ name: 'websites:sales-overview', params: to.params }),
  },
  {
    path: 'sales/overview',
    name: 'websites:sales-overview',
    component: () => import('./views/SalesOverviewView.vue'),
    meta: { title: 'Sales Overview', requires: salesGate },
  },
  {
    path: 'sales/subscriptions',
    name: 'websites:sales-subscriptions',
    component: () => import('./views/SalesSubscriptionsView.vue'),
    meta: { title: 'Active Subscriptions', requires: salesGate },
  },
  {
    path: 'sales/transactions',
    name: 'websites:sales-transactions',
    component: () => import('./views/TransactionsView.vue'),
    meta: { title: 'Transactions', requires: salesGate },
  },
  {
    path: 'sales/revenue',
    name: 'websites:sales-revenue',
    component: () => import('./views/RevenueView.vue'),
    meta: { title: 'Revenue', requires: salesGate },
  },
  {
    path: 'sales/products',
    name: 'websites:sales-products',
    redirect: (to) => ({ name: 'websites:subscriptions', params: to.params }),
  },
]

// Served since the coupons' routes landed: the checkout does not redeem a code yet, but the codes and
// their figures are the backend's.
const couponRoutes: RouteRecordRaw[] = [
  // The price catalogue's second tab. A coupon's page holds its own tabs, like a plan's.
  {
    path: 'coupons',
    name: 'websites:coupons',
    component: () => import('./views/CouponsView.vue'),
    meta: { title: 'Coupons', requires: salesGate },
  },
  {
    path: 'coupons/create',
    name: 'websites:coupon-create',
    component: () => import('./views/CouponDetailView.vue'),
    meta: { title: 'Create Coupon', requires: salesGate },
  },
  {
    path: 'coupons/:couponId',
    name: 'websites:coupon',
    component: () => import('./views/CouponDetailView.vue'),
    meta: { title: 'Coupon', requires: salesGate },
  },
]

/**
 * The Communication block: the campaigns, their audiences and the do-not-mail list, served
 * whole since 2026-10-11. Their reads take `websites.members.view`, the roster's key, since every one of them
 * resolves members; the server refuses a write without `websites.members.manage`.
 */
const communicationGate = { permissions: OrgPermissions.WEBSITES_MEMBERS_VIEW }

const communicationRoutes: RouteRecordRaw[] = [
    {
      path: 'campaigns',
      name: 'websites:campaigns',
      component: () => import('./views/CampaignsListView.vue'),
      meta: { title: 'Campaigns', requires: communicationGate },
    },
    {
      path: 'campaigns/create',
      name: 'websites:campaign-create',
      component: () => import('./views/CampaignDetailView.vue'),
      meta: { title: 'Create Campaign', requires: communicationGate },
    },
    {
      path: 'campaigns/:campaignId',
      name: 'websites:campaign',
      component: () => import('./views/CampaignDetailView.vue'),
      meta: { title: 'Campaign', requires: communicationGate },
    },
    {
      path: 'audiences',
      name: 'websites:audiences',
      component: () => import('./views/AudiencesListView.vue'),
      meta: { title: 'Audiences', requires: communicationGate },
    },
    {
      path: 'audiences/create',
      name: 'websites:audience-create',
      component: () => import('./views/AudienceDetailView.vue'),
      meta: { title: 'Create Audience', requires: communicationGate },
    },
    {
      path: 'suppressions',
      name: 'websites:suppressions',
      component: () => import('./views/SuppressionsListView.vue'),
      meta: { title: 'Do-not-mail list', requires: communicationGate },
    },
    {
      path: 'audiences/:audienceId',
      name: 'websites:audience',
      component: () => import('./views/AudienceDetailView.vue'),
      meta: { title: 'Audience', requires: communicationGate },
    },
  ]

export const websitesRoutes: RouteRecordRaw[] = [
  {
    path: 'websites',
    name: 'websites',
    component: () => import('./views/WebsitesListView.vue'),
    meta: {
      title: 'Websites',
      requires: { permissions: OrgPermissions.WEBSITES_WEBSITE_MANAGE },
    },
  },
  // Parcours de création - Plein écran, hors WebsiteLayout : le split-screen a
  // besoin de toute la largeur, et une route permet de reprendre un brouillon.
  {
    path: 'websites/new',
    name: 'websites:create',
    component: () => import('./views/CreateWebsiteView.vue'),
    meta: {
      title: 'Create a channel',
      fullWidth: true,
      // On a phone the wizard is the whole screen: no bottom bar, its cross is the way out.
      creationFlow: true,
      // Opening a channel is its own key, and it is not the one that manages an existing one.
      requires: { permissions: OrgPermissions.WEBSITES_WEBSITE_CREATE },
    },
  },
  // Editor - Full width (outside WebsiteLayout)
  {
    path: 'websites/:websiteId/editor',
    name: 'websites:editor',
    component: () => import('./views/ChannelEditorView.vue'),
    meta: {
      title: 'Editor',
      fullWidth: true,
    },
  },
  {
    path: 'websites/:websiteId',
    component: () => import('./layout/WebsiteLayout.vue'),
    meta: {
      title: 'Website',
      // The drawer reads the channel, its features and its hosts, and all three are gated on
      // this key: without it the shell itself would draw nothing.
      requires: { permissions: OrgPermissions.WEBSITES_WEBSITE_MANAGE },
    },
    children: [
      {
        path: '',
        redirect: { name: 'websites:dashboard' },
      },
      {
        path: 'dashboard',
        name: 'websites:dashboard',
        component: () => import('./views/WebsiteDashboardView.vue'),
        meta: {
          title: 'Dashboard',
        },
      },
      {
        path: 'statistics',
        name: 'websites:statistics',
        component: () => import('./views/StatisticsView.vue'),
        meta: {
          title: 'Statistics',
          // Its own key, held apart from the channel's: the six reads behind this screen refuse
          // a caller without it, and every panel would draw an empty answer that looked like an
          // audience of nobody.
          requires: { permissions: OrgPermissions.WEBSITES_STATS_VIEW },
        },
      },
      // Categories routes
      {
        path: 'categories',
        name: 'websites:categories',
        component: () => import('./views/CategoriesView.vue'),
        meta: {
          title: 'Categories',
          requires: catalogueGate,
        },
      },
      {
        path: 'categories/:categoryId',
        name: 'websites:category',
        component: () => import('./views/CategoryDetailView.vue'),
        meta: {
          title: 'Category',
          requires: catalogueGate,
        },
      },
      {
        path: 'items',
        name: 'websites:items',
        component: () => import('./views/ItemsListView.vue'),
        meta: {
          title: 'Items',
          requires: catalogueGate,
        },
      },
      {
        path: 'redirects',
        name: 'websites:redirects',
        component: () => import('./views/RedirectsView.vue'),
        meta: {
          title: 'Redirects',
          requires: catalogueGate,
        },
      },
      {
        path: 'comments',
        name: 'websites:comments',
        component: () => import('./views/CommentsListView.vue'),
        meta: {
          title: 'Comments',
          requires: commentsGate,
        },
      },
      // Blog routes
      {
        path: 'blog',
        name: 'websites:blog',
        component: () => import('./views/BlogListView.vue'),
        meta: {
          title: 'Blog',
          requires: blogGate,
        },
      },
      {
        path: 'blog/create',
        name: 'websites:blog-create',
        component: () => import('./views/BlogArticleView.vue'),
        meta: {
          title: 'New Article',
          requires: blogGate,
        },
      },
      {
        path: 'blog/:articleUid',
        name: 'websites:blog-article',
        component: () => import('./views/BlogArticleView.vue'),
        meta: {
          title: 'Article',
          requires: blogGate,
        },
      },
      {
        path: 'items/:itemId',
        name: 'websites:item',
        component: () => import('./views/ItemDetailView.vue'),
        meta: {
          title: 'Item',
          requires: catalogueGate,
        },
      },
      // Users routes
      {
        path: 'users',
        name: 'websites:users',
        component: () => import('./views/UsersListView.vue'),
        meta: {
          title: 'Users',
        },
      },
      {
        path: 'invitations',
        name: 'websites:invitations',
        component: () => import('./views/InvitationsListView.vue'),
        meta: {
          title: 'Invitations',
        },
      },
      {
        path: 'users/:userId',
        name: 'websites:user',
        component: () => import('./views/UserDetailView.vue'),
        meta: {
          title: 'User',
        },
        children: [
          {
            path: '',
            redirect: (to) => ({
              name: 'websites:user-overview',
              params: to.params,
            }),
          },
          {
            path: 'overview',
            name: 'websites:user-overview',
            component: () => import('./components/users/UserOverviewTab.vue'),
            meta: {
              title: 'Overview',
            },
          },
          {
            path: 'purchases',
            name: 'websites:user-purchases',
            component: () => import('./components/users/UserPurchasesTab.vue'),
            meta: {
              title: 'Purchases',
            },
          },
          {
            path: 'information',
            name: 'websites:user-information',
            component: () => import('./components/users/UserInformationTab.vue'),
            meta: {
              title: 'Information',
            },
          },
          {
            path: 'access',
            name: 'websites:user-access',
            component: () => import('./components/users/UserAccessTab.vue'),
            meta: {
              title: 'Access',
            },
          },
          // A member's own viewing on this channel. The tab is drawn only for a caller who also
          // holds `websites.members.view`, which the read asks too.
          {
            path: 'statistics',
            name: 'websites:user-statistics',
            component: () => import('./components/users/UserStatisticsTab.vue'),
            meta: {
              title: 'Statistics',
              requires: { permissions: OrgPermissions.WEBSITES_STATS_VIEW },
            },
          },
        ],
      },
      // Groups routes
      {
        path: 'groups',
        name: 'websites:groups',
        component: () => import('./views/GroupsListView.vue'),
        meta: {
          title: 'Groups',
        },
      },
      {
        path: 'groups/:groupId',
        name: 'websites:group',
        component: () => import('./views/GroupDetailView.vue'),
        meta: {
          title: 'Group',
        },
        children: [
          {
            path: '',
            redirect: (to) => ({
              name: 'websites:group-information',
              params: to.params,
            }),
          },
          {
            path: 'information',
            name: 'websites:group-information',
            component: () => import('./components/groups/GroupInformationTab.vue'),
            meta: {
              title: 'Information',
            },
          },
          {
            path: 'members',
            name: 'websites:group-members',
            component: () => import('./components/groups/GroupMembersTab.vue'),
            meta: {
              title: 'Members',
            },
          },
          {
            path: 'access',
            name: 'websites:group-access',
            component: () => import('./components/groups/GroupAccessTab.vue'),
            meta: {
              title: 'Access',
            },
          },
        ],
      },
      {
        path: 'access-requests',
        name: 'websites:access-requests',
        component: () => import('./views/AccessRequestsView.vue'),
        meta: {
          title: 'Access Requests',
        },
      },
      // The three things a visitor leaves and an operator answers. The deposits and the
      // messages are the two the redesign never gave a screen; the drawer hides each behind the
      // switch that lets a visitor leave one at all.
      {
        path: 'submissions',
        name: 'websites:submissions',
        component: () => import('./views/SubmissionsView.vue'),
        meta: {
          title: 'Submissions',
        },
      },
      {
        path: 'messages',
        name: 'websites:messages',
        component: () => import('./views/MessagesView.vue'),
        meta: {
          title: 'Messages',
        },
      },
      // The channel's subscription plans — what it sells, not what it has sold.
      // **A plan's subscriber list is the sales screens'**: it reads subscriptions, which no
      // backend serves, so its tab exists only with the development switch on.
      //
      // The detail screen holds its own tabs rather than child routes, like the category's: its
      // tabs are views of one plan, and the form on the first must survive a visit to another.
      {
        path: 'subscriptions',
        name: 'websites:subscriptions',
        component: () => import('./views/SubscriptionsView.vue'),
        meta: {
          title: 'Subscriptions',
        },
      },
      {
        path: 'subscriptions/create',
        name: 'websites:subscription-create',
        component: () => import('./views/SubscriptionDetailView.vue'),
        meta: {
          title: 'Create Subscription',
        },
      },
      {
        path: 'subscriptions/:planId',
        name: 'websites:subscription',
        component: () => import('./views/SubscriptionDetailView.vue'),
        meta: {
          title: 'Subscription',
        },
      },
      ...salesRoutes,
      ...couponRoutes,
      ...communicationRoutes,
      {
        path: 'settings',
        name: 'websites:settings',
        component: () => import('./views/SettingsView.vue'),
        meta: {
          title: 'Settings',
        },
        children: [
          {
            path: '',
            redirect: (to) => ({
              name: 'websites:settings-information',
              params: to.params,
            }),
          },
          {
            path: 'information',
            name: 'websites:settings-information',
            component: () => import('./views/settings/InformationView.vue'),
            meta: {
              title: 'Information',
            },
          },
          {
            path: 'domain',
            name: 'websites:settings-domain',
            component: () => import('./views/settings/DomainView.vue'),
            meta: {
              title: 'Domain',
            },
          },
          {
            path: 'authentication',
            name: 'websites:settings-authentication',
            component: () => import('./views/settings/AuthenticationView.vue'),
            meta: {
              title: 'Authentication',
            },
          },
          {
            path: 'template',
            name: 'websites:settings-template',
            component: () => import('./views/settings/TemplateView.vue'),
            meta: {
              title: 'Template',
            },
          },
          {
            path: 'options',
            name: 'websites:settings-options',
            component: () => import('./views/settings/OptionsView.vue'),
            meta: {
              title: 'Options',
            },
          },
          {
            path: 'notifications',
            name: 'websites:settings-notifications',
            component: () => import('./views/settings/NotificationsView.vue'),
            meta: {
              title: 'Notifications',
            },
          },
          {
            path: 'monetization',
            name: 'websites:settings-monetization',
            component: () => import('./views/settings/MonetizationView.vue'),
            meta: {
              title: 'Monetization',
            },
          },
          {
            path: 'legal',
            name: 'websites:settings-legal',
            component: () => import('./views/settings/LegalView.vue'),
            meta: {
              title: 'Legal information',
            },
          },
        ],
      },
    ],
  },
]

export default websitesRoutes
